import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Camera, Trash2, Loader2 } from "lucide-react";

const MAX_OUTPUT_SIDE = 320; // px
const QUALITY = 0.85;

async function resizeToDataUrl(file) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const side = Math.min(MAX_OUTPUT_SIDE, Math.min(img.width, img.height));
  const sx = (img.width - side) / 2;
  const sy = (img.height - side) / 2;
  const canvas = document.createElement("canvas");
  canvas.width = side; canvas.height = side;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, sx, sy, side, side, 0, 0, side, side);
  return canvas.toDataURL("image/jpeg", QUALITY);
}

export default function AvatarUploader() {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErr("Iba obrázky (JPG/PNG/WEBP)");
      return;
    }
    setBusy(true); setErr("");
    try {
      const dataUrl = await resizeToDataUrl(file);
      await api.put("/auth/avatar", { image_base64: dataUrl });
      await refresh();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Chyba pri nahrávaní");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true); setErr("");
    try {
      await api.delete("/auth/avatar");
      await refresh();
    } finally { setBusy(false); }
  };

  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();

  return (
    <div className="flex items-center gap-4" data-testid="avatar-uploader">
      <div className="relative w-20 h-20 border-2 border-gold-700/50 overflow-hidden bg-zinc-900 flex items-center justify-center">
        {user?.picture ? (
          <img src={user.picture} alt="avatar" className="w-full h-full object-cover" data-testid="avatar-image" />
        ) : (
          <span className="font-display font-black text-2xl text-gold-500">{initials}</span>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-gold-400" />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <input ref={inputRef} type="file" accept="image/*" onChange={upload}
          className="hidden" data-testid="avatar-file-input" />
        <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="avatar-upload-btn"
          className="wb-btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1.5">
          <Camera className="w-3.5 h-3.5" />
          {user?.picture ? t.profile.avatarChange : t.profile.avatarUpload}
        </button>
        {user?.picture && (
          <button onClick={remove} disabled={busy} data-testid="avatar-remove-btn"
            className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1">
            <Trash2 className="w-3 h-3" /> {t.profile.avatarRemove}
          </button>
        )}
        {err && <span className="text-xs text-red-400">{err}</span>}
        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-600">JPG / PNG · max 320×320 · auto-resize</span>
      </div>
    </div>
  );
}
